import { create } from 'zustand';

import LocationEntity from 'Models/Entities/LocationEntity';
import RouteEntity from 'Models/Entities/RouteEntity';
import SegmentEntity from 'Models/Entities/SegmentEntity';
import StopEntity from 'Models/Entities/StopEntity';
import { JourneyElementsDto } from './Types/JourneyElementsDto';

interface JourneyElementsFacts {
	hasMultiStopRoutes: boolean;
}

interface JourneyElementsStore {
	// states
	isLoading: boolean;
	errors?: any;

	// entities
	locations: LocationEntity[];
	routes: RouteEntity[];
	segments: SegmentEntity[];
	stops: StopEntity[];
	facts: JourneyElementsFacts;

	// setup functions
	setElements(elements: JourneyElementsDto): void;
	setErrors(errors: any): void;

	// helper functions
	getRouteById(routeId: string): RouteEntity | undefined;
	getLocationById(locationId: string): LocationEntity | undefined;
	getStopId(stopId: string): StopEntity | undefined;
}

export const journeyElementsStore = create<JourneyElementsStore, [['zustand/persist', unknown]]>((set, get) => ({
	isLoading: true,
	errors: {},
	locations: [],
	routes: [],
	segments: [],
	stops: [],
	facts: {
		hasMultiStopRoutes: false,
	},
	setElements(elements: JourneyElementsDto) {
		set({
			...linkJourneyElementAssociations(elements),
			isLoading: false,
		});
	},
	setErrors(errors: any) {
		set({ errors, isLoading: false });
	},
	getRouteById(routeId) {
		return get().routes.find(x => x.id === routeId);
	},
	getLocationById(locationId) {
		return get().locations.find(x => x.id === locationId);
	},
	getStopId(stopId) {
		return get().stops.find(x => x.id === stopId);
	},
	hasMultiStopRoutes() {
		const firstRouteWithMultipleStops = get().routes.find(route => route.stops && route.stops.length > 2);
		return firstRouteWithMultipleStops !== undefined;
	},
}));

const linkJourneyElementAssociations = ({ locations, routes, segments, stops }: JourneyElementsDto) => {
	for (const route of routes) {
		const departure = locations.find(x => x.id === route.departureId);
		if (departure) {
			route.departure = departure;
			if (!departure.departureRoutes) {
				departure.departureRoutes = [];
			}
			if (!departure.departureRoutes.includes(route)) {
				departure.departureRoutes.push(route);
			}
		}
		const destination = locations.find(x => x.id === route.destinationId);
		if (destination) {
			route.destination = destination;
			if (!destination.destinationRoutes) {
				destination.destinationRoutes = [];
			}
			if (!destination.destinationRoutes.includes(route)) {
				destination.destinationRoutes.push(route);
			}
		}
	}

	for (const segment of segments) {
		const route = routes.find(x => x.id === segment.routeId);
		if (route) {
			segment.route = route;
			if (!route.segments) {
				route.segments = [];
			}
			route.segments.push(segment);
		}
		const startStop = stops.find(x => x.id === segment.startStopId);
		if (startStop) {
			segment.startStop = startStop;
			if (!startStop.startSegments) {
				startStop.startSegments = [];
			}
			startStop.startSegments.push(segment);
		}
		const endStop = stops.find(x => x.id === segment.endStopId);
		if (endStop) {
			segment.endStop = endStop;
			if (!endStop.endSegments) {
				endStop.endSegments = [];
			}
			endStop.endSegments.push(segment);
		}
	}

	for (const stop of stops) {
		const route = routes.find(x => x.id === stop.routeId);
		if (route) {
			stop.route = route;
			if (!route.stops) {
				route.stops = [];
			}
			route.stops.push(stop);
		}
		const location = locations.find(x => x.id === stop.locationId);
		if (location) {
			stop.location = location;
			if (!stop.location.stops) {
				stop.location.stops = [];
			}
			stop.location.stops.push(stop);
		}
	}

	// Sort segments
	for (const route of routes) {
		if (!route.segments) {
			route.segments = [];
		}
		route.segments = route.segments.slice().sort((a, b) => {
			if (a.startStop.order < b.startStop.order) return -1;
			if (a.startStop.order > b.startStop.order) return 1;
			return 0;
		});
	}

	// Facts
	const firstRouteWithMultipleStops = routes.find(route => route.stops && route.stops.length > 2);
	const hasMultiStopRoutes = firstRouteWithMultipleStops !== undefined;

	return {
		locations,
		routes,
		segments,
		stops,
		facts: { hasMultiStopRoutes },
	};
};
